import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Form, Table, Button, Spinner, Alert, InputGroup } from 'react-bootstrap';
import { userService } from '../../services/api';
import { calculateAge, parseBirthDateFromFiscalCode, isMinor } from '../../utils/userHelpers';
import moment from 'moment';

const SearchParentUserModal = ({
  show,
  onHide,
  onSelectUser,
  currentUserId = null
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectingUserId, setSelectingUserId] = useState(null);
  const [error, setError] = useState('');
  const searchTimeoutRef = useRef(null);

  const isAdultUser = useCallback((user) => {
    const age = calculateAge(user.dateOfBirth, user.fiscalCode);
    if (age !== null) {
      return age >= 18;
    }
    return !isMinor(user.dateOfBirth, user.fiscalCode);
  }, []);

  const searchUsers = useCallback(async (queryText) => {
    setLoading(true);
    setError('');

    try {
      const response = await userService.getUsers({
        search: queryText?.trim() || null,
        page: 1,
        limit: 50,
        archiveFilter: 'active'
      });

      if (response.success && response.data?.items) {
        const matchingUsers = response.data.items.filter((u) => {
          // Exclude the current athlete being edited
          if (currentUserId && String(u.id) === String(currentUserId)) {
            return false;
          }
          // Include only adults
          return isAdultUser(u);
        });

        setUsers(matchingUsers);
      } else {
        setUsers([]);
        if (!response.success && response.message) {
          setError(response.message);
        }
      }
    } catch (err) {
      console.error('Error searching users for parent form:', err);
      setError('Errore durante la ricerca degli utenti');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [currentUserId, isAdultUser]);

  // Trigger search on debounced search term or on opening modal
  useEffect(() => {
    if (!show) {
      setSearchTerm('');
      setUsers([]);
      setError('');
      return;
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchUsers(searchTerm);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, show, searchUsers]);

  const handleSelectUser = async (user) => {
    setSelectingUserId(user.id);
    setError('');

    try {
      // Fetch full details of the user to get address, birthMunicipality, residencePostalCode
      const fullRes = await userService.getUser(user.id);
      const userToUse = fullRes.success && fullRes.data ? fullRes.data : user;
      onSelectUser(userToUse);
      onHide();
    } catch (err) {
      console.error('Error fetching full user data for parent prefill:', err);
      // Fallback to basic user data
      onSelectUser(user);
      onHide();
    } finally {
      setSelectingUserId(null);
    }
  };

  const formatDateOfBirth = (user) => {
    if (user.dateOfBirth) {
      return moment(user.dateOfBirth).format('DD/MM/YYYY');
    }
    const parsed = parseBirthDateFromFiscalCode(user.fiscalCode);
    if (parsed) {
      return moment(parsed).format('DD/MM/YYYY');
    }
    return '-';
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fas fa-search me-2 text-primary"></i>
          Cerca Atleta / Utente Registrato
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p className="text-muted small mb-3">
          Cerca un utente maggiorenne registrato nel sistema per compilare automaticamente i dati del genitore/tutore.
        </p>

        <Form.Group className="mb-3" controlId="searchParentUser">
          <InputGroup>
            <InputGroup.Text>
              <i className="fas fa-search text-muted"></i>
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Cerca per nome, cognome o codice fiscale..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
              data-testid="search-parent-user-input"
            />
            {searchTerm && (
              <Button
                variant="outline-secondary"
                onClick={() => setSearchTerm('')}
                title="Cancella ricerca"
              >
                <i className="fas fa-times"></i>
              </Button>
            )}
          </InputGroup>
        </Form.Group>

        {error && (
          <Alert variant="danger" onClose={() => setError('')} dismissible>
            {error}
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" variant="primary" />
            <span className="ms-2">Ricerca utenti in corso...</span>
          </div>
        ) : users.length > 0 ? (
          <div className="table-responsive" style={{ maxHeight: '380px' }}>
            <Table hover align="middle" className="mb-0" data-testid="parent-users-table">
              <thead className="table-light sticky-top">
                <tr>
                  <th>Nome e Cognome</th>
                  <th>Data di Nascita</th>
                  <th>Email</th>
                  <th className="text-end">Azione</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <div className="fw-semibold">
                        {u.firstName} {u.lastName}
                      </div>
                      {u.fiscalCode && (
                        <div className="text-muted small font-monospace">
                          {u.fiscalCode}
                        </div>
                      )}
                    </td>
                    <td>{formatDateOfBirth(u)}</td>
                    <td>
                      <div>{u.email || u.loginEmail || '-'}</div>
                      {u.phoneNumber && (
                        <div className="text-muted small">
                          <i className="fas fa-phone-alt me-1"></i>
                          {u.phoneNumber}
                        </div>
                      )}
                    </td>
                    <td className="text-end">
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => handleSelectUser(u)}
                        disabled={selectingUserId === u.id}
                        data-testid={`select-user-${u.id}`}
                      >
                        {selectingUserId === u.id ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-1" />
                            Selezione...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-check me-1"></i>
                            Seleziona
                          </>
                        )}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-4 text-muted">
            <i className="fas fa-user-slash fa-2x mb-2 d-block text-secondary opacity-50"></i>
            {searchTerm.trim()
              ? 'Nessun utente maggiorenne trovato con questo criterio di ricerca.'
              : 'Nessun utente maggiorenne disponibile.'}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Annulla
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SearchParentUserModal;
